import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowLeft, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { paxityAPI } from "@/lib/api";
import { DeliveryOptions, defaultDeliveryMode } from "@/components/DeliveryOptions";
import { orderNo } from "@/lib/utils";
import { t, getLocale, cartDisplayTotal, unitAmount } from "@/lib/locale";
import { loadPaxityV2Widget } from "@/lib/paxityWidget";
import { PaxityCardPanel } from "@/components/checkout/PaxityCardPanel";
import { findCountry, countryName, STATES } from "@/lib/countries";
import { normalizeEmail } from "@/lib/email";
import { colorName } from "@/lib/colors";
import { OPERATOR_META } from "@/components/checkout/operatorMeta";
import { CheckoutSuccess } from "@/components/checkout/CheckoutSuccess";
import { CheckoutPending } from "@/components/checkout/CheckoutPending";
import { AddressStep } from "@/components/checkout/AddressStep";
import { PaymentMethodPicker } from "@/components/checkout/PaymentMethodPicker";
import { PaxityPhoneForm } from "@/components/checkout/PaxityPhoneForm";
import { CheckoutSummary } from "@/components/checkout/CheckoutSummary";

// La transaction en attente est persistée : si le client part payer dans
// l'app Wave/Orange Money et que le navigateur recharge la page au retour,
// on restaure l'attente et on affiche la confirmation dès que c'est payé.
const PENDING_TX_KEY = "sec_pending_paxity_tx_v1";

// Libellé complet d'un article pour la commande : nom + taille + couleur
// choisies par le client (visibles partout : dashboard vendeur, tickets, emails).
const itemLabel = (it) => {
    const opts = [it.size ? `Taille ${it.size}` : null, it.color ? colorName(it.color) : null].filter(Boolean);
    return opts.length ? `${it.name} — ${opts.join(" · ")}` : it.name;
};
// Confirmation persistée en session : survit au remontage du composant
// (changement de langue/devise) et au rechargement de la page.
const COMPLETE_TX_KEY = "sec_completed_paxity_tx_v1";

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    // Synchronisation étapes ↔ historique navigateur : chaque « Continuer »
    // pousse une entrée d'historique ; le bouton RETOUR du navigateur revient
    // donc à l'étape précédente, exactement comme les boutons Retour du site.
    const goToStep = (n) => {
        window.history.pushState(
            { ...(window.history.state || {}), idx: (window.history.state?.idx ?? 0) + 1, checkoutStep: n },
            "",
        );
        setStep(n);
    };
    const goBackStep = () => window.history.back();
    useEffect(() => {
        const onPop = (e) => {
            const s = e.state?.checkoutStep;
            setStep(typeof s === "number" ? s : 1);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);
    const [deliveryMode, setDeliveryMode] = useState(defaultDeliveryMode);
    const shipping = 0;
    const total = subtotal + shipping;
    // Paxity (Wave, Orange Money et Carte) n'encaisse QUE en F CFA : on débite
    // toujours le prix vendeur F CFA et on affiche l'équivalent €/$ en disclaimer.
    const localizedCartTotal = getLocale().currency === "XOF" ? null : cartDisplayTotal(items);
    // La carte est encaissée en F CFA : Paxity ne route QUE le XOF (leur API
    // refuse EUR/USD avec « unsupported corridor » et le paiement échoue).
    // Le bouton reste affiché dans la devise du client, sans conversion.
    const cardChargeCurrency = "XOF";
    const cardChargeAmount = total;

    const [buyer, setBuyer] = useState(() => {
        // Pays pré-sélectionné depuis la géolocalisation IP (si connu)
        const detected = findCountry(getLocale().country);
        return {
            firstName: "", lastName: "", email: "",
            phone: "", address: "", zip: "", city: "", state: "",
            country: detected ? detected.code : "SN",
        };
    });

    const selectedCountry = findCountry(buyer.country);

    const [paxityConfig, setPaxityConfig] = useState(null);
    const [paxityError, setPaxityError] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [prefix, setPrefix] = useState(() => findCountry(getLocale().country)?.dial || "221");

    const changeCountry = (code) => {
        setBuyer((b) => ({ ...b, country: code, state: "" }));
        const c = findCountry(code);
        if (c) setPrefix(c.dial); // indicatif appliqué automatiquement
    };

    const [processing, setProcessing] = useState(false);
    const [transaction, setTransaction] = useState(null); // { transaction_id, status, order_id, ... }
    const [complete, setComplete] = useState(false);

    // Remonter en haut de page à chaque changement d'étape (adresse → livraison
    // → paiement) et à l'affichage des écrans attente/confirmation : sur mobile,
    // le clic « Continuer » se fait en bas de page et la vue restait en bas.
    useEffect(() => {
        window.scrollTo(0, 0); // forme universelle (compatible tous mobiles)
    }, [step, complete, transaction?.status]);
    const [checkingNow, setCheckingNow] = useState(false);
    // Échec carte chez Paxity : on bascule le client vers Wave / Orange Money
    // au lieu de le laisser dans un cul-de-sac.
    const [cardIssue, setCardIssue] = useState(false);

    // Restaurer une transaction en attente (retour depuis l'app de paiement)
    // ou une confirmation récente (remontage/rechargement juste après paiement)
    const restoredOnce = useRef(false);
    useEffect(() => {
        if (restoredOnce.current) return;
        restoredOnce.current = true;
        try {
            // La confirmation est gardée en localStorage (PAS sessionStorage) :
            // au retour de l'app Wave/OM, le téléphone réouvre souvent le site
            // dans un nouvel onglet — le sessionStorage était perdu et le client
            // tombait sur « Panier vide » après avoir payé.
            const done = localStorage.getItem(COMPLETE_TX_KEY);
            if (done) {
                const tx = JSON.parse(done);
                const tooOld = tx?.at && Date.now() - tx.at > 6 * 60 * 60 * 1000;
                if (items.length > 0 || tooOld) {
                    // Le client démarre une NOUVELLE commande (ou confirmation
                    // trop ancienne) : on oublie l'ancienne confirmation.
                    localStorage.removeItem(COMPLETE_TX_KEY);
                } else if (tx?.order_id) {
                    setTransaction(tx);
                    setComplete(true);
                    return; // confirmation prioritaire sur toute restauration pending
                }
            }
        } catch {
            localStorage.removeItem(COMPLETE_TX_KEY);
        }
        try {
            const raw = localStorage.getItem(PENDING_TX_KEY);
            if (!raw) return;
            const tx = JSON.parse(raw);
            if (tx?.transaction_id && tx.status === "pending") setTransaction(tx);
            else localStorage.removeItem(PENDING_TX_KEY);
        } catch {
            localStorage.removeItem(PENDING_TX_KEY);
        }
    }, [items.length]);

    // Persister tant que le paiement est en attente
    useEffect(() => {
        if (!transaction) return;
        if (transaction.status === "pending") {
            localStorage.setItem(PENDING_TX_KEY, JSON.stringify(transaction));
        } else {
            localStorage.removeItem(PENDING_TX_KEY);
        }
    }, [transaction]);

    // Persister la confirmation : elle doit survivre à un remontage du composant
    // (changement de langue/devise détecté) ou à un rechargement de la page.
    useEffect(() => {
        if (complete && transaction?.order_id) {
            localStorage.setItem(COMPLETE_TX_KEY, JSON.stringify({ ...transaction, status: "success", at: Date.now() }));
        }
    }, [complete, transaction]);

    // Fetch backend config
    useEffect(() => {
        paxityAPI.getConfig()
            .then((cfg) => {
                setPaxityConfig(cfg);
                // Pré-sélection du moyen de paiement : l'opérateur du pays du
                // client (indicatif) si disponible, sinon le premier. L'indicatif
                // téléphone NE suit PAS l'opérateur : il reste celui du pays
                // sélectionné (ex. États-Unis → +1). Il passe à 221/225 seulement
                // quand le client choisit explicitement Wave / Orange Money.
                const methods = cfg.methods || [];
                if (getLocale().currency !== "XOF") {
                    // Hors zone F CFA : seule la carte bancaire est proposée.
                    setPaymentMethod("CARD");
                    return;
                }
                const detectedDial = findCountry(getLocale().country)?.dial;
                const first = methods.find((m) => m.prefix === detectedDial) || methods[0];
                if (first) setPaymentMethod(first.code);
            })
            .catch(() => setPaxityError("Impossible de contacter le service de paiement."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // L'API Paxity n'accepte AUCUNE URL de retour (doc officielle) : le retour
    // vers notre site est donc géré ici, différemment selon l'appareil.
    // - MOBILE (Android/iPhone) : AUCUNE nouvelle fenêtre. Le lien s'ouvre dans
    //   le même onglet → le système ouvre l'app Wave/OM par-dessus le site.
    //   Au retour du client, la transaction en attente est restaurée depuis
    //   localStorage (PENDING_TX_KEY) et confirmée par polling.
    // - ORDINATEUR : onglet séparé pendant que cette page reste en attente
    //   active ; dès que Paxity confirme, l'onglet de paiement est fermé
    //   automatiquement et le client retrouve notre confirmation.
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const payWinRef = useRef(null);
    const closePayWindow = () => {
        try {
            payWinRef.current?.close();
        } catch (e) {
            console.debug("[Paxity] fermeture onglet paiement impossible", e?.message);
        }
        payWinRef.current = null;
    };
    const openPayWindow = (url) => {
        if (isMobileDevice) {
            // Même onglet : pas de fenêtre supplémentaire sur téléphone
            window.location.href = url;
            return;
        }
        const w = window.open(url, "_blank");
        if (w) {
            payWinRef.current = w;
        } else {
            // Pop-up bloqué : repli dans le même onglet
            window.location.href = url;
        }
    };

    // Poll status while pending — vérifie immédiatement, puis toutes les 3,5s,
    // et dès que le client revient sur l'onglet (retour de l'app Wave/OM).
    useEffect(() => {
        if (!transaction || transaction.status !== "pending") return;
        let stopped = false;
        const checkNow = async () => {
            try {
                const res = await paxityAPI.getStatus(transaction.transaction_id);
                if (stopped) return;
                if (res.status !== transaction.status) {
                    // Merge the full response so amount/order_id survive the
                    // cart clear() and render correctly on the confirmation.
                    setTransaction((prev) => ({ ...prev, ...res }));
                }
                if (res.status === "success") {
                    closePayWindow();
                    setComplete(true);
                    clear();
                    localStorage.removeItem(PENDING_TX_KEY);
                    toast.success(t("Paiement confirmé ✦"), { description: `${t("Commande")} ${orderNo(res.order_id)}` });
                } else if (res.status === "failed") {
                    closePayWindow();
                    localStorage.removeItem(PENDING_TX_KEY);
                    toast.error(t("Paiement échoué"), { description: t("Veuillez réessayer") });
                }
            } catch (e) {
                // Erreur réseau passagère pendant le polling : on retentera au tick suivant
                console.debug("[Paxity] polling status indisponible, nouvelle tentative…", e?.message);
            }
        };
        checkNow();
        // Vérification rapide (2s) pour le mobile money ; un peu plus espacée
        // pour la carte (chaque appel interroge la liste Paxity).
        const interval = setInterval(checkNow, transaction.is_card ? 5000 : 2000);
        const onVisible = () => {
            if (document.visibilityState === "visible") checkNow();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            stopped = true;
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [transaction, clear]);

    // Bouton « J'ai payé — Vérifier » sur l'écran d'attente
    const manualCheck = async () => {
        if (!transaction?.transaction_id) return;
        setCheckingNow(true);
        try {
            const res = await paxityAPI.getStatus(transaction.transaction_id);
            if (res.status === "success") {
                closePayWindow();
                setTransaction((prev) => ({ ...prev, ...res }));
                setComplete(true);
                clear();
                localStorage.removeItem(PENDING_TX_KEY);
                toast.success(t("Paiement confirmé ✦"), { description: `${t("Commande")} ${orderNo(res.order_id)}` });
            } else if (res.status === "failed") {
                closePayWindow();
                setTransaction((prev) => ({ ...prev, ...res }));
                localStorage.removeItem(PENDING_TX_KEY);
                toast.error(t("Paiement échoué"), { description: t("Veuillez réessayer") });
            } else {
                toast(t("Paiement toujours en attente"), {
                    description: t("Validez la transaction sur votre téléphone, puis revérifiez."),
                });
            }
        } catch {
            toast.error(t("Vérification impossible"), { description: t("Vérifiez votre connexion et réessayez.") });
        } finally {
            setCheckingNow(false);
        }
    };

    const buyerValid = () =>
        buyer.firstName && buyer.lastName && normalizeEmail(buyer.email) && buyer.phone && buyer.address && buyer.city &&
        (!STATES[buyer.country] || buyer.state);

    // Wave et Orange Money n'existent qu'en zone F CFA : les visiteurs
    // Europe / USA / Canada ne voient QUE la carte bancaire.
    const mobileMoneyAllowed = getLocale().currency === "XOF";
    const mobileMethods = mobileMoneyAllowed ? (paxityConfig?.methods || []) : [];
    const selectedMethod = mobileMethods.find((m) => m.code === paymentMethod);
    const operatorIconMeta = (selectedMethod && OPERATOR_META[selectedMethod.icon]) || OPERATOR_META.wave;


    // Paiement CARTE via le widget hébergé Paxity v2 : le backend crée la
    // commande + la session (token), le widget s'ouvre en modale par-dessus la
    // page (saisie carte + 3DS côté Paxity). À `onSuccess`, on confirme la
    // commande côté serveur et on affiche la confirmation.
    const handleCardPayment = async () => {
        localStorage.removeItem(COMPLETE_TX_KEY); // nouvelle commande : oublier l'ancienne confirmation
        setProcessing(true);
        setPaxityError(null);
        try {
            // Carte : TOUJOURS encaissée en F CFA. Paxity ne route que le XOF
            // (EUR/USD → « unsupported corridor » et le paiement échoue).
            const payCurrency = "XOF";
            const chargedAmount = total;
            const res = await paxityAPI.cardInit({
                amount: chargedAmount,
                currency: payCurrency,
                base_amount_xof: total,
                delivery_mode: deliveryMode,
                description: `Commande Shopping en Chine — ${items.length} article(s)`,
                customer: {
                    name: `${buyer.firstName} ${buyer.lastName}`.trim(),
                    email: buyer.email,
                    city: buyer.city,
                    phone: buyer.phone ? `+${prefix} ${buyer.phone}` : undefined,
                    address: [buyer.address, buyer.zip, buyer.state, selectedCountry ? countryName(selectedCountry) : null].filter(Boolean).join(", ") || undefined,
                },
                items: items.map((it) => {
                    const localUnit = payCurrency === "XOF" ? null : unitAmount(it);
                    return {
                        product_id: it.id,
                        name: itemLabel(it),
                        price: it.price,
                        qty: it.qty,
                        color: it.color || undefined,
                        size: it.size || undefined,
                        // Prix unitaire saisi par le vendeur dans la devise payée (EUR/USD)
                        price_paid: localUnit != null ? Math.round(localUnit * 100) / 100 : undefined,
                    };
                }),
            });

            const Paxity = await loadPaxityV2Widget(res.widget_script);

            // Le 3-D Secure sur téléphone peut quitter notre site (page blanche
            // au retour) : la transaction est mémorisée ici pour être restaurée
            // et vérifiée auprès du serveur dès que le client revient.
            const pendingCard = {
                transaction_id: res.transaction_id,
                order_id: res.order_id,
                status: "pending",
                operator_label: t("Carte bancaire"),
                amount: res.amount,
                currency: res.currency,
                is_card: true,
            };
            localStorage.setItem(PENDING_TX_KEY, JSON.stringify(pendingCard));
            setTransaction(pendingCard);

            // Confirmation serveur après succès du widget : finalise la commande
            // (numéro définitif, stock, emails) puis affiche la confirmation.
            const finish = async () => {
                try {
                    const done = await paxityAPI.cardConfirm({
                        transaction_id: res.transaction_id,
                        order_id: res.order_id,
                    });
                    const finalOrder = done.order_id || res.order_id;
                    if (done.status !== "success") {
                        // Paxity n'a pas encore basculé la transaction : le
                        // polling de l'écran d'attente prend le relais.
                        toast(t("Paiement en cours de vérification…"));
                        return;
                    }
                    setTransaction({
                        transaction_id: res.transaction_id,
                        order_id: finalOrder,
                        status: "success",
                        operator_label: t("Carte bancaire"),
                        amount: res.amount,
                        currency: res.currency,
                    });
                    setComplete(true);
                    clear();
                    localStorage.removeItem(PENDING_TX_KEY);
                    toast.success(t("Paiement confirmé ✦"), { description: `${t("Commande")} ${orderNo(finalOrder)}` });
                } catch (e) {
                    console.error("[PaxityCard] confirm error", e);
                    toast.error(t("Paiement reçu — confirmation en cours"), { description: t("Votre commande sera validée sous peu.") });
                }
            };

            Paxity.open({
                token: res.token,
                default_method: res.default_method || "CARD",
                closeOnResult: true,
                onSuccess: finish,
                onFailure: (reason) => {
                    console.debug("[PaxityCard] échec", reason);
                    localStorage.removeItem(PENDING_TX_KEY);
                    setTransaction(null);
                    setCardIssue(true);
                },
                onCancel: () => {
                    // Le client a fermé la fenêtre sans payer : on retire l'attente.
                    localStorage.removeItem(PENDING_TX_KEY);
                    setTransaction(null);
                },
                onError: (message) => {
                    console.error("[PaxityCard] widget error", message);
                    localStorage.removeItem(PENDING_TX_KEY);
                    setTransaction(null);
                    setCardIssue(true);
                },
            });
        } catch (err) {
            console.error("[PaxityCard] init error", err);
            toast.error(t("Paiement carte indisponible"), {
                description: err.response?.data?.detail || t("Réessayez ou utilisez Mobile Money."),
            });
        } finally {
            setProcessing(false);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        localStorage.removeItem(COMPLETE_TX_KEY); // nouvelle commande : oublier l'ancienne confirmation

        // ---- Client-side validation ----
        const cleanPhone = buyer.phone.replace(/\D/g, "");
        const expectedLengths = {
            "221": [9],       // Sénégal
            "225": [10],      // Côte d'Ivoire
        };
        const validLengths = expectedLengths[prefix] || [8, 9, 10];
        if (!validLengths.includes(cleanPhone.length)) {
            const expected = validLengths.join(" ou ");
            toast.error(t("Numéro de téléphone invalide"), {
                description: `${t("Pour l'indicatif")} +${prefix}, ${t("le numéro doit contenir")} ${expected} ${t("chiffres")}. ${t("Vous avez saisi")} ${cleanPhone.length} ${t("chiffres")}.`,
            });
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                amount: total,
                phone_number: buyer.phone.replace(/\s+/g, ""),
                prefix_phone: prefix,
                payment_method: paymentMethod,
                description: `Commande Shopping en Chine · ${items.length} article(s)`,
                delivery_mode: deliveryMode,
                customer: {
                    name: `${buyer.firstName} ${buyer.lastName}`,
                    email: buyer.email,
                    city: buyer.city,
                    phone: `+${prefix} ${buyer.phone}`,
                    address: [buyer.address, buyer.zip, buyer.state, selectedCountry ? countryName(selectedCountry) : null].filter(Boolean).join(", ") || undefined,
                },
                items: items.map((it) => ({
                    product_id: it.id,
                    name: itemLabel(it),
                    price: it.price,
                    qty: it.qty,
                    color: it.color || undefined,
                    size: it.size || undefined,
                })),
            };

            const res = await paxityAPI.createPayin(payload);

            setTransaction({ ...res, operator_label: operatorIconMeta.label });
            if (res.status === "success") {
                setComplete(true);
                clear();
                toast.success(t("Paiement confirmé ✦"));
            } else if (res.status === "pending") {
                toast(t("Paiement en cours…"), { description: t("Validez la transaction sur votre téléphone.") });
            } else {
                toast.error(t("Paiement refusé"), { description: res.message || t("Réessayez ou changez de moyen.") });
            }
        } catch (err) {
            // Show a customer-friendly French message. Technical details
            // (API keys, hosts, .env, status codes) must NEVER reach shoppers —
            // they are logged to the console for the merchant/support instead.
            const status = err.response?.status;
            const rawDetail = err.response?.data?.detail || err.response?.data?.message;
            console.error("[Paiement] Échec Paxity", { status, detail: rawDetail, error: err.message });

            let detail;
            if (status === 400 && typeof rawDetail === "string" && rawDetail) {
                // Actionable validation errors (OTP requis, montant invalide,
                // méthode inconnue…) are already written for the customer.
                detail = rawDetail;
            } else if (err.code === "ECONNABORTED") {
                detail = "Le paiement a mis trop de temps à répondre. Veuillez réessayer.";
            } else if (!err.response) {
                detail = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
            } else {
                detail = "Le paiement n'a pas pu être traité pour le moment. Veuillez réessayer dans quelques instants ou choisir un autre moyen de paiement.";
            }

            toast.error(t("Erreur de paiement"), { description: detail });
            setPaxityError(detail);
        } finally {
            setProcessing(false);
        }
    };

    // ---------- Success screen ----------
    if (complete) {
        return <CheckoutSuccess transaction={transaction} total={total} />;
    }

    // ---------- Pending screen ----------
    if (transaction && transaction.status === "pending") {
        return (
            <CheckoutPending
                transaction={transaction}
                operatorLabel={operatorIconMeta.label}
                onOpenPay={openPayWindow}
                onManualCheck={manualCheck}
                checkingNow={checkingNow}
                onCancel={() => { closePayWindow(); localStorage.removeItem(PENDING_TX_KEY); setTransaction(null); }}
            />
        );
    }

    // ---------- Empty cart guard ----------
    if (items.length === 0) {
        return (
            <div className="container mx-auto px-5 py-24 text-center">
                <h1 className="font-display text-4xl mb-3">{t("Panier vide")}</h1>
                <p className="text-muted-foreground mb-6">{t("Ajoutez des produits avant de commander.")}</p>
                <Button asChild className="rounded-full"><Link to="/boutique">{t("Voir la boutique")}</Link></Button>
            </div>
        );
    }

    const steps = [
        { n: 1, label: t("Adresse") },
        { n: 2, label: t("Livraison") },
        { n: 3, label: t("Paiement") },
    ];

    return (
        <div className="container mx-auto px-5 py-10 md:py-14">
            <Link to="/panier" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <ArrowLeft className="h-4 w-4" /> {t("Retour au panier")}
            </Link>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-8">{t("Commande")}</h1>

            {/* Stepper */}
            <div className="flex items-center gap-2 sm:gap-4 mb-10">
                {steps.map((s, i) => (
                    <div key={s.n} className="flex items-center gap-2 sm:gap-4 flex-1">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${step >= s.n ? "bg-ink text-ink-foreground" : "bg-muted text-muted-foreground"}`}>
                            {step > s.n ? <Check className="h-4 w-4" /> : s.n}
                        </div>
                        <span className={`text-sm font-medium ${step >= s.n ? "text-foreground" : "text-muted-foreground"} hidden sm:inline`}>{s.label}</span>
                        {i < steps.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-ink" : "bg-border"}`} />}
                    </div>
                ))}
            </div>

            <div className="grid lg:grid-cols-[1fr_380px] gap-10">
                <div className="space-y-8 order-2 lg:order-1">
                    {step === 1 && (
                        <AddressStep
                            buyer={buyer}
                            setBuyer={setBuyer}
                            prefix={prefix}
                            selectedCountry={selectedCountry}
                            onCountryChange={changeCountry}
                            onContinue={() => {
                                if (!buyerValid()) {
                                    toast.error(
                                        buyer.email && !normalizeEmail(buyer.email)
                                            ? t("Adresse e-mail invalide — vérifiez-la, c'est là que part votre confirmation.")
                                            : t("Veuillez remplir tous les champs requis")
                                    );
                                    return;
                                }
                                goToStep(2);
                            }}
                        />
                    )}

                    {step === 2 && (
                        <div className="space-y-5 bg-card p-6 md:p-8 rounded-2xl shadow-card">
                            <h2 className="font-display text-2xl">{t("Mode de livraison")}</h2>
                            <DeliveryOptions value={deliveryMode} onChange={setDeliveryMode} />
                            <div className="flex gap-2 pt-2">
                                <Button type="button" variant="outline" onClick={goBackStep} className="rounded-full h-11 px-6">{t("Retour")}</Button>
                                <Button type="button" onClick={() => goToStep(3)} className="bg-ink text-ink-foreground hover:bg-ink/90 rounded-full h-11 px-8">{t("Continuer")}</Button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-5 bg-card p-6 md:p-8 rounded-2xl shadow-card">
                            <div className="flex items-center justify-between">
                                <h2 className="font-display text-2xl">{t("Paiement")}</h2>
                            </div>

                            {cardIssue && (
                                <div
                                    data-testid="card-unavailable-alert"
                                    className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm"
                                >
                                    <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="font-medium">{t("Le paiement par carte a échoué")}</p>
                                        <p className="text-xs mt-1 opacity-90">
                                            {mobileMoneyAllowed
                                                ? t("Notre prestataire bancaire ne traite pas les cartes pour le moment. Payez en quelques secondes avec Wave ou Orange Money — votre panier est conservé.")
                                                : t("Veuillez réessayer")}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {["WAVE", "ORANGE"].map((family) =>
                                                mobileMethods.find((m) => m.code.startsWith(family))
                                            ).filter(Boolean).map((m) => (
                                                <Button
                                                    key={m.code}
                                                    type="button"
                                                    size="sm"
                                                    data-testid={`card-fallback-${m.code}`}
                                                    onClick={() => { setCardIssue(false); setPaymentMethod(m.code); }}
                                                    className="rounded-full bg-ink text-ink-foreground hover:bg-ink/90"
                                                >
                                                    {t("Payer avec")} {m.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {paxityConfig && !paxityConfig.configured && (
                                <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                                    <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium">{t("Paiement momentanément indisponible")}</p>
                                        <p className="text-xs mt-1 opacity-90">
                                            {t("Le paiement en ligne est en cours de maintenance. Veuillez réessayer dans quelques instants.")}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {paxityError && (
                                <div data-testid="paxity-error-banner" className="flex gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                    <XCircle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div className="flex-1">{paxityError}</div>
                                </div>
                            )}

                            <PaymentMethodPicker
                                methods={mobileMethods}
                                value={paymentMethod}
                                onSelect={(m) => {
                                    setPaymentMethod(m.code);
                                    if (m.prefix !== "*") setPrefix(m.prefix);
                                }}
                                onSelectCard={paxityConfig?.card_enabled ? () => setPaymentMethod("CARD") : undefined}
                            />

                            {paymentMethod === "CARD" && (
                                <PaxityCardPanel
                                    total={cardChargeAmount}
                                    currency={cardChargeCurrency}
                                    localizedTotal={localizedCartTotal}
                                    processing={processing}
                                    onBack={goBackStep}
                                    onPay={handleCardPayment}
                                />
                            )}

                            {selectedMethod && (
                                <PaxityPhoneForm
                                    buyer={buyer}
                                    setBuyer={setBuyer}
                                    prefix={prefix}
                                    setPrefix={setPrefix}
                                    processing={processing}
                                    disabled={!paxityConfig?.configured}
                                    total={total}
                                    onSubmit={handlePayment}
                                    onBack={goBackStep}
                                />
                            )}
                        </div>
                    )}
                </div>

                <CheckoutSummary items={items} />
            </div>
        </div>
    );
}
